import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ReactTooltip from "react-tooltip";
import swal from "sweetalert";
import SuperTableV2 from "../SuperTableV2";

registerLocale("es", es);

// Excluye sábados y domingos de los datepicker de la matriz.
const isWeekday = (date) => {
  const day = date.getDay();
  return day !== 0 && day !== 6;
};

// Un campo es obligatorio si lo marca estáticamente (required) o si su condición dinámica
// (requiredIf, ej. FechaCumplimiento solo si Estado = Cumplido) se cumple con el formData actual.
const isFieldRequired = (f, formData) =>
  !!f.required || (typeof f.requiredIf === "function" && f.requiredIf(formData));

// Formato mínimo de correo (usuario@dominio.tld). Se valida solo si el campo trae valor:
// un campo "email" no obligatorio puede quedar vacío, pero si se llena debe ser válido.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Render anti-XSS: descripciones / texto libre del usuario SIEMPRE como texto plano.
// Mismo patrón del repo (H-05): nunca dangerouslySetInnerHTML.
export const toPlainText = (val) => {
  if (val === null || val === undefined) return "";
  return String(val)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "");
};

// Componente genérico CRUD para las 3 matrices simétricas de artefactos (riesgos/compromisos/novedades).
// Cada matriz lo configura con sus columnas, campos de formulario y servicio. No sobre-diseñado:
// solo abstrae la maquinaria común (tabla + modal crear/editar + confirmación de borrado + estado Redux).
//
// Props:
//  - idPortafolio: string (IdPortafolio del padre)
//  - idField: nombre del campo PK de la matriz (ej. "IdRiesgo")
//  - columns: [{ key, captionKey, type? }] columnas de la tabla (type: "text"|"plain"|"number")
//  - fields:  [{ name, labelKey, type, required?, options?, optionsKey? }] campos del formulario del modal
//             type: "text" | "textarea" | "number" | "date" | "select"
//             options (select estático): [{ value, labelKey }] — label vía i18n
//             optionsKey (select dinámico): clave en dynamicOptions; las opciones ya traen { value, label } literal
//  - service: { create, update, remove } service de la matriz
//  - items, loading, error: sub-slice Redux de la matriz
//  - fetchAction: thunk de recarga (idPortafolio) => action
//  - dynamicOptions: { [optionsKey]: [{ value, label }] } opciones resueltas en runtime (proyectos, DDL)
const ArtifactTable = ({
  idPortafolio,
  idField,
  columns,
  fields,
  service,
  items,
  loading,
  error,
  fetchAction,
  dynamicOptions = {},
  emptyState,
}) => {
  const [t] = useTranslation("briefcase");
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (idPortafolio) {
      dispatch(fetchAction(idPortafolio));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idPortafolio]);

  // El modal (y sus campos con data-tip) se monta/desmonta dinámicamente: hay que
  // reconstruir los tooltips globales para que enganchen los nuevos nodos del DOM.
  useEffect(() => {
    ReactTooltip.rebuild();
  });

  const openCreate = () => {
    setEditingId(null);
    // Aplica valores por defecto declarados en fields (ej. Retrospectiva = No).
    const initial = {};
    fields.forEach((f) => {
      if (f.type === "section") return;
      if (f.default !== undefined) initial[f.name] = f.default;
    });
    setFormData(initial);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingId(row[idField]);
    // Hidrata solo los campos del formulario (sin metadatos calculados/no editables).
    const initial = {};
    fields.forEach((f) => {
      if (f.type === "section") return;
      let value = row[f.name];
      // Normaliza datetime a yyyy-MM-dd para input date y datepicker.
      if ((f.type === "date" || f.type === "datepicker") && value) {
        value = String(value).slice(0, 10);
      }
      // Normaliza BIT de BD (true/false de mssql) o INT (1/0) a entero estricto para yesno.
      if (f.type === "yesno") {
        initial[f.name] = value === true || value === 1 ? 1 : value === false || value === 0 ? 0 : null;
      } else {
        initial[f.name] = value ?? "";
      }
    });
    setFormData(initial);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({});
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Devuelve null si todo es válido, o el motivo ("required" | "email") del primer campo que falla.
  const validate = () => {
    for (const f of fields) {
      if (f.type === "section") continue;
      const v = formData[f.name];
      const isEmpty = v === undefined || v === null || String(v).trim() === "";
      if (isFieldRequired(f, formData) && isEmpty) return "required";
      if (!isEmpty && f.type === "email" && !EMAIL_REGEX.test(String(v).trim())) return "email";
    }
    return null;
  };

  const buildPayload = () => {
    const payload = {};
    fields.forEach((f) => {
      if (f.type === "section") return;
      let value = formData[f.name];
      if (value === "" || value === undefined) {
        value = null;
      } else if (f.type === "number" && value !== null) {
        const n = Number(value);
        value = Number.isNaN(n) ? null : n;
      }
      payload[f.name] = value;
    });
    return payload;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      swal(
        t("artefactos_error_title"),
        t(
          validationError === "email"
            ? "artefactos_validation_email"
            : "artefactos_validation_required"
        ),
        "warning"
      );
      return;
    }
    try {
      setSaving(true);
      const payload = buildPayload();
      if (editingId) {
        await service.update(idPortafolio, editingId, payload);
      } else {
        await service.create(idPortafolio, payload);
      }
      swal(
        t("artefactos_done_title"),
        editingId ? t("artefactos_done_update") : t("artefactos_done_create"),
        "success"
      );
      closeModal();
      dispatch(fetchAction(idPortafolio));
    } catch (e) {
      swal(t("artefactos_error_title"), t("artefactos_error_save"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (row) => {
    swal({
      title: t("artefactos_swal_delete_title"),
      text: t("artefactos_swal_delete_text"),
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (confirm) => {
      if (!confirm) return;
      try {
        await service.remove(idPortafolio, row[idField]);
        swal(t("artefactos_done_title"), t("artefactos_done_delete"), "success");
        dispatch(fetchAction(idPortafolio));
      } catch (e) {
        swal(t("artefactos_error_title"), t("artefactos_error_delete"), "error");
      }
    });
  };

  // Columna de acciones (editar / eliminar) + columnas de datos. Texto libre como texto plano.
  const tableColumns = [
    {
      caption: t("artefactos_actions"),
      selector: "__actions",
      width: 110,
      align: "center",
      customRender: (rowWrap) => {
        const row = rowWrap.data;
        return (
          <div className="d-flex justify-content-center">
            <Button
              variant="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={() => openEdit(row)}
              title={t("artefactos_btn_edit")}
            >
              <i className="fa fa-pen" />
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDelete(row)}
              title={t("artefactos_btn_delete")}
            >
              <i className="fa fa-trash" />
            </Button>
          </div>
        );
      },
    },
    ...columns.map((col) => ({
      caption: t(col.captionKey),
      selector: col.key,
      width: col.width || 160,
      align: col.align || (col.type === "number" ? "center" : undefined),
      customRender: (rowWrap) => {
        const raw = rowWrap.data[col.key];
        if (col.type === "number") {
          return <span>{raw ?? ""}</span>;
        }
        if (col.type === "date") {
          return <span>{raw ? String(raw).slice(0, 10) : ""}</span>;
        }
        // text / plain → texto plano anti-XSS
        return <span title={toPlainText(raw)}>{toPlainText(raw)}</span>;
      },
    })),
  ];

  return (
    <div>

      {loading ? (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" variant="danger" />
        </div>
      ) : error ? (
        <p className="text-center text-muted py-4">{t("artefactos_error_load")}</p>
      ) : items && items.length > 0 ? (
        <SuperTableV2
          data={items}
          columns={tableColumns}
          title_searchbox={t("artefactos_searchbox")}
        />
      ) : emptyState ? (
        <div className="text-center pt-4 mt-4">
          <img
            src={emptyState.image}
            alt=""
            className="img-fluid mb-3 animated pulse slower infinite"
            width="200"
          />
          <h5>{t(emptyState.titleKey)}</h5>
          <p>{t(emptyState.descKey)}</p>
          <Button onClick={openCreate}>
            <i className="fa fa-plus-circle mr-2"></i>
            {t(emptyState.btnKey)}
          </Button>
        </div>
      ) : (
        <p className="text-center text-muted py-4">{t("artefactos_empty")}</p>
      )}

      {items && items.length > 0 && (
        <Button
          variant="success"
          className="position-fixed shadow animate__animated animate__fadeInUp"
          style={{ bottom: "1rem", right: "1rem" }}
          onClick={openCreate}
        >
          <i className="fa fa-plus-circle mr-2" />
          {emptyState ? t(emptyState.btnKey) : t("artefactos_btn_new")}
        </Button>
      )}

      <Modal show={showModal} onHide={closeModal} centered size="lg" contentClassName="border-0 shadow-lg">
        <Modal.Header>
          <Modal.Title className="h5">
            {editingId
              ? t("artefactos_modal_edit_title")
              : t("artefactos_modal_create_title")}
          </Modal.Title>
          <Button variant="light" onClick={closeModal} aria-label="Close">
            X
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {(() => {
              // Agrupa los fields por sección para que el fondo gris envuelva header + campos.
              const groups = [];
              let cur = { section: null, fields: [] };
              fields.forEach((f) => {
                if (f.type === "section") {
                  if (cur.section || cur.fields.length > 0) groups.push(cur);
                  cur = { section: f, fields: [] };
                } else {
                  cur.fields.push(f);
                }
              });
              if (cur.section || cur.fields.length > 0) groups.push(cur);

              const renderField = (f) => {
                const colMd = f.colWidth != null ? f.colWidth : f.type === "textarea" ? 12 : 6;
                return (
                  <Col md={colMd} key={f.name} className="mb-3 d-flex flex-column">
                    <Form.Label>
                      {t(f.labelKey)}
                      {isFieldRequired(f, formData) && <span className="text-primary"> *</span>}
                      {f.tooltipKey && !(f.hideTooltipOnEdit && editingId) && (
                        <i
                          className="fa fa-question-circle text-info ml-2"
                          style={{ fontSize: "0.85rem" }}
                          data-tip={t(f.tooltipKey)}
                          data-class="pwc-tooltip shadow-lg py-3 tip-to-front"
                        ></i>
                      )}
                    </Form.Label>
                    <div className="mt-auto">
                    {f.type === "textarea" ? (
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={formData[f.name] || ""}
                        onChange={(e) => handleChange(f.name, e.target.value)}
                      />
                    ) : f.type === "select" ? (
                      <Form.Control
                        as="select"
                        value={formData[f.name] || ""}
                        onChange={(e) => handleChange(f.name, e.target.value)}
                      >
                        <option value="">{t(f.placeholderKey)}</option>
                        {f.optionsKey
                          ? (dynamicOptions[f.optionsKey] || []).map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))
                          : (f.options || []).map((opt) => (
                              <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                            ))}
                      </Form.Control>
                    ) : f.type === "datepicker" ? (
                      <DatePicker
                        selected={formData[f.name] ? new Date(formData[f.name] + "T00:00:00") : null}
                        onChange={(date) => {
                          if (!date) {
                            handleChange(f.name, "");
                          } else {
                            const y = date.getFullYear();
                            const m = String(date.getMonth() + 1).padStart(2, "0");
                            const d = String(date.getDate()).padStart(2, "0");
                            handleChange(f.name, `${y}-${m}-${d}`);
                          }
                        }}
                        dateFormat="dd/MM/yyyy"
                        className="form-control"
                        placeholderText="dd/mm/aaaa"
                        autoComplete="off"
                        isClearable
                        popperProps={{ strategy: "fixed" }}
                        locale="es"
                        filterDate={isWeekday}
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={80}
                      />
                    ) : f.type === "yesno" ? (
                      <div className="d-flex align-items-center" style={{ height: "38px", gap: "8px" }}>
                        <Button
                          type="button"
                          variant={formData[f.name] === 1 ? "success" : "outline-secondary"}
                          size="sm"
                          style={{ minWidth: "56px" }}
                          onClick={() => handleChange(f.name, 1)}
                        >
                          {t("artefactos_yesno_si")}
                        </Button>
                        <Button
                          type="button"
                          variant={formData[f.name] === 0 ? "danger" : "outline-secondary"}
                          size="sm"
                          style={{ minWidth: "56px" }}
                          onClick={() => handleChange(f.name, 0)}
                        >
                          {t("artefactos_yesno_no")}
                        </Button>
                      </div>
                    ) : (
                      <Form.Control
                        type={
                          f.type === "number"
                            ? "number"
                            : f.type === "date"
                            ? "date"
                            : f.type === "email"
                            ? "email"
                            : "text"
                        }
                        value={formData[f.name] || ""}
                        onChange={(e) => {
                          if (f.type === "number") {
                            handleChange(f.name, e.target.value.replace(/[^0-9]/g, ""));
                          } else {
                            handleChange(f.name, e.target.value);
                          }
                        }}
                        {...(f.type === "number" ? {
                          min: 1,
                          step: 1,
                          onKeyDown: (e) => {
                            if ([".", "-", "+", "e", "E"].includes(e.key)) e.preventDefault();
                          },
                          onPaste: (e) => {
                            const text = e.clipboardData.getData("text");
                            if (!/^\d+$/.test(text)) e.preventDefault();
                          },
                        } : {})}
                      />
                    )}
                    </div>
                    {typeof f.hintWhen === "function" && f.hintWhen(formData) && (
                      <div className="small text-primary mt-1">
                        <i className="fa fa-info-circle mr-1" />
                        {t(f.hintKey)}
                      </div>
                    )}
                  </Col>
                );
              };

              return groups.map((group, gi) => {
                const fieldCols = group.fields.map(renderField);

                if (group.section) {
                  return (
                    <div
                      key={group.section.name}
                      className="mb-3"
                      style={{ backgroundColor: "#f3f3f3", borderRadius: "5px" }}
                    >
                      <div className="px-3 py-2 d-flex align-items-center">
                        {group.section.icon && (
                          <i className={`fa fa-${group.section.icon} mr-2 text-primary`} />
                        )}
                        <h6 className="text-primary mb-0">{t(group.section.labelKey)}</h6>
                      </div>
                      <div className="px-3 pt-2">
                        <Row>{fieldCols}</Row>
                      </div>
                    </div>
                  );
                }

                return <Row key={`g-${gi}`}>{fieldCols}</Row>;
              });
            })()}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="dark" onClick={closeModal} disabled={saving}>
            <i className="fa fa-times mr-2" />
            {t("artefactos_btn_cancel")}
          </Button>
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <i className="fa fa-save mr-2" />
                {t("artefactos_btn_save")}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ArtifactTable;
